// LandPrepPopup.jsx

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  IconButton,
  Grid,
  InputLabel,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { makeStyles } from "@mui/styles";
import DatePicker from "react-datepicker";
import CustomButton from "../../Custom/CustomButton";
import instance from "../../../globals/axios";
import { getFarmerPlotId } from "../../../globals/utils";
import { useAlert } from "../../Alert";
import EditIcon from "../../../assets/svg/EditIcon.svg";
import SuccessPopup from "./SuccessPopup";

const useStyles = makeStyles((theme) => ({
  closeButton: {
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(1),
    fontSize: 16,
  },
  dialogContent: {
    height: 400,
  },
  datepicker: {
    display: "none",
  },
  taxtLabel: {
    fontSize: "12px !important",
    fontFamily: "Inter",
    fontWeight: "400 !important",
    color: "#000",
    marginTop: "10px",
  },
  taxtTitle: {
    fontSize: "12px !important",
    fontFamily: "Inter",
    fontWeight: "700 !important",
    color: "#000",
  },
}));

const LenoBagsPopup = ({ onClose, t }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(true);
  const [issueDate, setIssueDate] = useState(null);
  const [bagsIssued, setBagsIssued] = useState("");
  const [returnDate, setReturnDate] = useState(null);
  const [bagsReturned, setBagsReturned] = useState("");
  const [existingData, setExistingData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [comment, setComment] = useState("");
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [errors, setErrors] = useState({
    issueDate: "",
    returnDate: "",
    bagsIssued: "",
    bagsReturned: "",
  });
  // TODO: Handle AO later if required
  let role = "FARMER";
  const { showAlert } = useAlert();
  let farmerPlotId = getFarmerPlotId();

  const sections = [
    {
      label: t("Issue"),
      date: issueDate,
      setDate: setIssueDate,
      bags: bagsIssued,
      setBags: setBagsIssued,
    },
    {
      label: t("Return"),
      date: returnDate,
      setDate: setReturnDate,
      bags: bagsReturned,
      setBags: setBagsReturned,
    },
  ];
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await instance(
          `plotengagement/get-leno-bags?farmer_plot=${farmerPlotId}`
        );
        let data = response.data;
        if (data.length > 0) {
          setExistingData(data[0]);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    if (farmerPlotId) {
      fetchData();
    }
  }, [farmerPlotId]);
  useEffect(() => {
    if (existingData) {
      if (existingData) {
        setBagsIssued(existingData.bags_issued || "");
        setBagsReturned(existingData.bags_returned || "");
        setComment(existingData.comments || "");
        setIssueDate(
          existingData.issue_date ? new Date(existingData.issue_date) : null
        );
        setReturnDate(
          existingData.return_date ? new Date(existingData.return_date) : null
        );
      }
    }
  }, [existingData]);
  const handleDateChange = (index, date) => {
    const { setDate } = sections[index];
    setDate(date);
    setErrors((prevErrors) => ({
      ...prevErrors,
      [index === 0 ? "issueDate" : "returnDate"]: "",
    }));
  };
  const handleCommentChange = (event) => {
    setComment(event.target.value);
  };
  const handleEditClick = () => {
    setIsEditMode(true);
  };

  const handleBagsChange = (index, bags) => {
    const { setBags } = sections[index];
    setBags(bags);
    setErrors((prevErrors) => ({
      ...prevErrors,
      [index === 0 ? "bagsIssued" : "bagsReturned"]: "",
    }));
  };

  const validateForm = () => {
    let formValid = true;

    // Clear previous errors
    setErrors({
      issueDate: "",
      returnDate: "",
      bagsIssued: "",
      bagsReturned: "",
    });

    // Validation checks
    if (!issueDate && role === "AO") {
      setErrors((prevErrors) => ({
        ...prevErrors,
        issueDate: "Please select issue date.",
      }));
      formValid = false;
    }

    if (!returnDate && role === "FARMER") {
      setErrors((prevErrors) => ({
        ...prevErrors,
        returnDate: "Please select return date.",
      }));
      formValid = false;
    }

    if (bagsIssued === "" && role === "AO") {
      setErrors((prevErrors) => ({
        ...prevErrors,
        bagsIssued: "Please enter bags issued.",
      }));
      formValid = false;
    }

    if (bagsReturned === "" && role === "FARMER") {
      setErrors((prevErrors) => ({
        ...prevErrors,
        bagsReturned: "Please enter bags returned.",
      }));
      formValid = false;
    }

    return formValid;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      let formData = {};
      const formatDate = (date) => {
        const pad = (num) => (num < 10 ? `0${num}` : `${num}`);
        return date
          ? `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
              date.getDate()
            )}`
          : null;
      };
      if (role === "FARMER") {
        formData = {
          return_date: formatDate(returnDate),
          bags_returned: parseInt(bagsReturned),
          comments: comment,
        };
      } else {
        formData = {
          issue_date: formatDate(issueDate),
          bags_issued: parseInt(bagsIssued),
          return_date: formatDate(returnDate),
          bags_returned: parseInt(bagsReturned),
          comments: comment,
        };
      }

      if (isEditMode) {
        formData.id = existingData.id;
      } else {
        formData.farmer_plot = farmerPlotId;
      }
      const apiUrl = isEditMode
        ? "plotengagement/update-leno-bags"
        : "plotengagement/create-leno-bags";
      instance
        .post(apiUrl, formData)
        .then((res) => {
          if (res?.data) {
            setShowSuccessPopup(true);
            setTimeout(() => {
              setShowSuccessPopup(false);
              onClose();
            }, 2000);
          }
        })
        .catch((err) => {
          console.error("error creating/updating leno-bags", err);
          showAlert("error", "Some Error Occured");
        });
    }
  };

  const handleClose = () => {
    setOpen(false);
    onClose();
  };
  return (
    <div>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="xs"
        style={{ zIndex: 1 }}
        PaperProps={{ style: { maxHeight: "calc(100% - 25%)" } }}
      >
        <div style={{ padding: "16px" }}>
          <Grid container>
            <Grid item xs={10}>
              <DialogTitle style={{ padding: "0px" }}>
                {t("Leno Bags")}
              </DialogTitle>
            </Grid>
            {existingData !== null ? (
              <Grid item xs={1} style={{ marginTop: "10px" }}>
                <img src={EditIcon} onClick={handleEditClick} />
              </Grid>
            ) : null}
            <Grid item xs={1}>
              <IconButton
                aria-label="close"
                className={classes.closeButton}
                onClick={handleClose}
              >
                <CloseIcon />
              </IconButton>
            </Grid>
          </Grid>
          {sections.map((section, index) => (
            <Grid container style={{ marginTop: "20px" }} key={index}>
              <Grid item xs={7} style={{ marginTop: "20px" }}>
                <InputLabel style={{ fontSize: "12px" }}>
                  {t(`${section.label} Date: `)}
                </InputLabel>
              </Grid>

              <Grid item xs={5}>
                <TextField
                  placeholder="DD/MM/YYYY"
                  fullWidth
                  value={section.date ? section.date.toLocaleDateString() : ""}
                  helperText={errors[index === 0 ? "issueDate" : "returnDate"]}
                  error={!!errors[index === 0 ? "issueDate" : "returnDate"]}
                  // disabled={!isEditMode && existingData !== null}
                  // disabled={(role === "FARMER" && section.label === "Issue") || (!isEditMode && existingData !== null)}
                  disabled={
                    (!(role === "AO") && existingData === null) ||
                    (!isEditMode && existingData && existingData.bags_issued) ||
                    (existingData &&
                      existingData.bags_returned &&
                      section.label === "Issue") ||
                    (role === "FARMER" && section.label !== "Return")
                  }
                  InputProps={{
                    sx: {
                      height: "40px",
                      borderRadius: "5px",
                      marginTop: "10px",
                      background: "#FFF",
                      border: "1px solid #C7BDBD",
                    },
                    readOnly: true,
                    onClick: () =>
                      document.getElementById(`date-picker-${index}`).click(),
                  }}
                />
                <DatePicker
                  id={`date-picker-${index}`}
                  dateFormat="dd/MM/yyyy"
                  selected={section.date}
                  onChange={(date) => handleDateChange(index, date)}
                  className={classes.datepicker}
                  style={{ display: "none" }}
                />
              </Grid>
              <Grid item xs={7}>
                <InputLabel
                  style={{ fontSize: "12px", marginTop: "25px" }}
                >{`Bags ${section.label}`}</InputLabel>
              </Grid>
              <Grid item xs={5}>
                <TextField
                  type="number"
                  // disabled={!isEditMode && existingData !== null}
                  // disabled={(role === "FARMER" && section.label === "Issue") || (!isEditMode && existingData !== null)}
                  disabled={
                    (!(role === "AO") && existingData === null) ||
                    (!isEditMode && existingData && existingData.bags_issued) ||
                    (existingData &&
                      existingData.bags_returned &&
                      section.label === "Issue") ||
                    (role === "FARMER" && section.label !== "Return")
                  }
                  value={section.bags}
                  onChange={(event) =>
                    handleBagsChange(index, event.target.value)
                  }
                  fullWidth
                  size="small"
                  helperText={
                    errors[index === 0 ? "bagsIssued" : "bagsReturned"]
                  }
                  error={!!errors[index === 0 ? "bagsIssued" : "bagsReturned"]}
                  InputProps={{
                    sx: {
                      height: "40px",
                      borderRadius: "5px",
                      marginTop: "15px",
                      background: "#FFF",
                      border: "1px solid #C7BDBD",
                    },
                  }}
                />
              </Grid>
            </Grid>
          ))}

          {/* Modal for displaying full-size image */}
          <div style={{ display: "grid", padding: "10px" }}>
            <InputLabel>{t("Comment (Optional)")}</InputLabel>
            <TextField
              multiline
              rows={1}
              value={comment}
              onChange={handleCommentChange}
              disabled={!isEditMode && existingData !== null}
            />
          </div>

          <div
            style={{
              display: "flex",
              margin: "auto",
              justifyContent: "center",
            }}
          >
            {existingData && isEditMode ? (
              <div
                style={{
                  display: "flex",
                  margin: "auto",
                  justifyContent: "center",
                  width: "140px",
                }}
              >
                <CustomButton onClick={handleSubmit} hideIcon>
                  {t("Update")}
                </CustomButton>
              </div>
            ) : !existingData && !isEditMode ? (
              <div
                style={{
                  display: "flex",
                  margin: "auto",
                  justifyContent: "center",
                  width: "140px",
                }}
              >
                <CustomButton onClick={handleSubmit} hideIcon>
                  {t("Submit")}
                </CustomButton>
              </div>
            ) : null}
          </div>
        </div>
        {showSuccessPopup && (
          <SuccessPopup onClose={() => setShowSuccessPopup(false)} />
        )}
      </Dialog>
    </div>
  );
};

export default LenoBagsPopup;
